(() => {
  'use strict';

  const config = window.distopionFreeGiftConfig;
  if (!config || !window.fetch || !window.Shopify?.routes?.root) return;

  const giftId = Number(config.variantId);
  const thresholdShopCurrency = Number(config.threshold);
  const shopCurrency = 'EUR';
  const giftHandle = String(config.handle || 'distopion-pouch');
  const root = window.Shopify.routes.root;
  const source = 'distopion-free-gift';
  const initialItemCount = Number(config.initialItemCount || 0);

  let pendingCart = null;
  let running = false;
  let readScheduled = false;
  let protectionFrame = 0;
  let protectionTimer = 0;
  let settledResolvers = [];

  const endpoint = (path) => `${root}${path}`;

  const readJson = async (response) => {
    const payload = await response.json().catch(() => null);
    if (!response.ok) {
      throw new Error(payload?.description || payload?.message || `Cart request failed (${response.status})`);
    }
    return payload;
  };

  const getCart = async () => {
    const response = await fetch(endpoint('cart.js'), {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' },
    });
    return readJson(response);
  };

  const postCart = async (path, data) => {
    const response = await fetch(endpoint(path), {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    });
    return readJson(response);
  };

  const giftLines = (cart) =>
    Array.isArray(cart?.items)
      ? cart.items.filter((item) => Number(item.variant_id) === giftId)
      : [];

  const merchandiseSubtotal = (cart) => {
    if (!Array.isArray(cart?.items)) return 0;

    const merchandise = cart.items.reduce((total, item) => {
      if (Number(item.variant_id) === giftId) return total;
      return total + Number(item.final_line_price || 0);
    }, 0);

    const cartDiscount = (cart.cart_level_discount_applications || []).reduce(
      (total, discount) => total + Number(discount.total_allocated_amount || 0),
      0
    );

    return Math.max(0, merchandise - cartDiscount);
  };

  const thresholdForCart = (cart) => {
    const cartCurrency = String(cart?.currency || '').toUpperCase();

    if (cartCurrency === shopCurrency) return thresholdShopCurrency;

    const activeCurrency = String(window.Shopify?.currency?.active || '').toUpperCase();
    const currencyRate = Number(window.Shopify?.currency?.rate);

    if (
      cartCurrency &&
      activeCurrency === cartCurrency &&
      Number.isFinite(currencyRate) &&
      currencyRate > 0
    ) {
      // Convert the EUR threshold into the shopper's presentment currency.
      // Round up so a cart just below EUR 100 never qualifies due to cents.
      return Math.ceil(thresholdShopCurrency * currencyRate);
    }

    // Fail closed if Shopify's active conversion rate is unavailable or does
    // not match the currency returned by the cart API.
    return Number.POSITIVE_INFINITY;
  };

  const publish = (cart) => {
    const detail = { cart, source };

    document.dispatchEvent(
      new CustomEvent('cart:change', {
        bubbles: true,
        detail,
      })
    );

    document.dispatchEvent(
      new CustomEvent('cart:refresh', {
        bubbles: true,
        detail,
      })
    );
  };

  const normalizeGift = async (cart) => {
    const lines = giftLines(cart);
    const eligible = merchandiseSubtotal(cart) >= thresholdForCart(cart);
    const alreadyCorrect =
      (!eligible && lines.length === 0) ||
      (eligible && lines.length === 1 && Number(lines[0].quantity) === 1);

    if (alreadyCorrect) return null;

    if (lines.length) {
      const updates = {};
      lines.forEach((line) => {
        updates[line.key] = 0;
      });
      await postCart('cart/update.js', { updates });
    }

    if (eligible) {
      await postCart('cart/add.js', {
        items: [
          {
            id: giftId,
            quantity: 1,
            properties: { _complimentary_gift: 'true' },
          },
        ],
      });
    }

    return getCart();
  };

  const resolveSettled = () => {
    if (running || pendingCart || readScheduled) return;
    const resolvers = settledResolvers;
    settledResolvers = [];
    resolvers.forEach((resolve) => resolve());
  };

  const whenSettled = () => {
    if (!running && !pendingCart && !readScheduled) return Promise.resolve();
    return new Promise((resolve) => settledResolvers.push(resolve));
  };

  const drain = async () => {
    if (running) return;
    running = true;

    try {
      while (pendingCart) {
        const cart = pendingCart;
        pendingCart = null;

        try {
          const finalCart = await normalizeGift(cart);

          if (finalCart) {
            publish(finalCart);
            requestProtection();

            // A shopper can change the cart while the gift request is in flight.
            // In that rare case, replace the stale queued event with the current
            // server cart before evaluating the rule again.
            if (pendingCart) pendingCart = await getCart();
          }
        } catch (error) {
          console.error('[Distopion complimentary pouch]', error);
        }
      }
    } finally {
      running = false;
      if (pendingCart) {
        queueMicrotask(drain);
      } else {
        resolveSettled();
      }
    }
  };

  const enqueue = (cart) => {
    if (!cart?.items) {
      scheduleRead();
      return;
    }

    pendingCart = cart;
    queueMicrotask(drain);
  };

  const scheduleRead = (delay = 0) => {
    if (readScheduled) return;
    readScheduled = true;

    window.setTimeout(async () => {
      readScheduled = false;
      try {
        enqueue(await getCart());
      } catch (error) {
        console.error('[Distopion complimentary pouch]', error);
        resolveSettled();
      }
    }, delay);
  };

  const protectGiftControls = () => {
    const escapedHandle = window.CSS?.escape ? CSS.escape(giftHandle) : giftHandle;
    const links = document.querySelectorAll(`a[href*="/products/${escapedHandle}"]`);

    links.forEach((link) => {
      const item = link.closest('line-item.line-item');
      if (!item) return;

      item.dataset.distopionFreeGift = 'true';
      item
        .querySelectorAll('.line-item__quantity-selector, quantity-selector, .line-item__remove-button')
        .forEach((control) => {
          control.hidden = true;
        });

      const drawerPrice = item.querySelector('.line-item__line-price');
      if (drawerPrice) drawerPrice.textContent = 'Complimentary';

      const row = item.closest('tr');
      if (!row) return;

      row.dataset.distopionFreeGift = 'true';
      const quantityControl = row.querySelector('td:nth-child(2) line-item');
      if (quantityControl) quantityControl.hidden = true;

      const total = row.querySelector('td:last-child span');
      if (total) total.textContent = 'Complimentary';
    });
  };

  const requestProtection = () => {
    if (!protectionFrame) {
      protectionFrame = requestAnimationFrame(() => {
        protectionFrame = 0;
        protectGiftControls();
      });
    }

    window.clearTimeout(protectionTimer);
    protectionTimer = window.setTimeout(protectGiftControls, 160);
  };

  const start = () => {
    requestProtection();

    document.addEventListener(
      'cart:change',
      (event) => {
        requestProtection();
        if (event.detail?.source === source) return;
        enqueue(event.detail?.cart);
      },
      { passive: true }
    );

    document.addEventListener('cart:refresh', requestProtection, { passive: true });

    document.addEventListener(
      'click',
      (event) => {
        const protectedItem = event.target.closest('[data-distopion-free-gift="true"]');
        if (!protectedItem) return;

        const control = event.target.closest(
          'quantity-selector a, .line-item__remove-button, a[href*="/cart/change"]'
        );
        if (!control) return;

        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );

    // Only delay checkout when a pouch correction is actively pending. Normal
    // checkout clicks have no added work or delay.
    document.addEventListener(
      'click',
      async (event) => {
        const checkout = event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (!checkout || (!running && !pendingCart && !readScheduled)) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        await whenSettled();

        const form = checkout.form || checkout.closest('form');
        if (form?.requestSubmit) {
          form.requestSubmit(checkout);
        } else if (form) {
          form.submit();
        } else {
          location.href = `${root}checkout`;
        }
      },
      true
    );

    // Empty carts need no startup request. Non-empty carts are checked only
    // after the browser is idle so page rendering and interaction win.
    if (initialItemCount > 0) {
      const initialCheck = () => scheduleRead();
      if ('requestIdleCallback' in window) {
        requestIdleCallback(initialCheck, { timeout: 1500 });
      } else {
        window.setTimeout(initialCheck, 600);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) scheduleRead(0);
  });
})();
